import { useEffect } from "react";

const SITE = "https://epsviewer.in";

/**
 * Per-page SEO. Sets <title>, meta description, canonical link, Open Graph
 * title/description/url, and (optionally) a JSON-LD structured-data block.
 *
 * Because the prerender step snapshots the rendered DOM, all of this is baked
 * into the static HTML for each route.
 */

/**
 * Every route is served as `<route>/index.html`, so Apache's DirectorySlash
 * 301-redirects `/faq` to `/faq/`. Canonicals must name the URL that actually
 * responds 200, otherwise every canonical points at a redirect.
 */
function withTrailingSlash(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export default function Seo({ title, description, path = "", jsonLd, noindex = false }) {
  useEffect(() => {
    const canonical = SITE + withTrailingSlash(path || window.location.pathname);

    if (title) document.title = title;

    // The 404 document is served for every unmatched URL, so it must never be
    // indexed in its own right.
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, follow");
    } else if (robots) {
      robots.remove();
    }

    const setMeta = (selector, attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);

    // Canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD structured data
    const existing = document.getElementById("page-jsonld");
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, jsonLd, noindex]);

  return null;
}
